import React, { useState, useEffect, useRef } from 'react';
import Icon from '@mui/material/Icon';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import { searchEntities } from '../customer/onboardingApi';

// ── Helpers ────────────────────────────────────────────────────
export function getEntityDisplayName(entity) {
    const a = entity.attributes || entity;
    const typeId = a.entity_type_id;
    const company = String(a.name || '').trim();
    const person = `${a.first_name || ''} ${a.last_name || ''}`.trim();
    const named = typeId === 2 ? company || person : person || company;
    // Never invent a name: blank records show their ID.
    return named || `Customer #${a.entity_id ?? entity.entity_id ?? entity.id ?? '?'}`;
}

export function getEntityCompany(entity) {
    const a = entity.attributes || entity;
    return (
        a?.Company?.Organization?.name ||
        a?.Company?.name ||
        ''
    );
}

export function getEntityTypeName(entity) {
    const a = entity.attributes || entity;
    return a?.EntityType?.type_name || 'Entity';
}

export function getEntityId(entity) {
    return entity.attributes?.entity_id ?? entity.entity_id ?? 0;
}

// ── Component ──────────────────────────────────────────────────
const EntitySearchPicker = ({
    onSelect,
    selectedEntity,
    placeholder = 'Search by name or company…',
    className = '',
}) => {
    const [query, setQuery] = useState('');
    const [allEntities, setAllEntities] = useState([]);
    const [filteredResults, setFilteredResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const containerRef = useRef(null);

    // Load all entities once
    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {
                const data = await searchEntities();
                setAllEntities(data);
            } catch (err) {
                console.error('Failed to load entities', err);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, []);

    // Filter entities locally based on query
    useEffect(() => {
        if (!query.trim()) {
            setFilteredResults([]);
            setShowDropdown(false);
            return;
        }

        const results = allEntities.filter(entity => {
            const name = getEntityDisplayName(entity).toLowerCase();
            const company = getEntityCompany(entity).toLowerCase();
            const q = query.trim().toLowerCase();
            const id = String(getEntityId(entity));
            return name.includes(q) || company.includes(q) || id === q.replace(/^#/, '');
        });

        setFilteredResults(results.slice(0, 25));
        setShowDropdown(true);
    }, [query, allEntities]);

    // Close on outside click
    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleSelect = (entity) => {
        onSelect(entity);
        setQuery('');
        setShowDropdown(false);
    };

    // ── Selected badge ─────────────────────────────────────────
    if (selectedEntity) {
        const typeId = (selectedEntity.attributes?.entity_type_id ?? selectedEntity.entity_type_id);
        return (
            <MDBox className={`flex items-center gap-3 bg-[#0F2714] border border-[#4ADE80]/50 rounded-lg px-4 py-3 ${className}`}>
                <MDBox className="w-9 h-9 rounded-full bg-[#1A3F2B] flex items-center justify-center shrink-0">
                    {typeId === 2 ? (
                        <i className="fa fa-building text-[#4ADE80]" />
                    ) : (
                        <i className="fa fa-user text-[#4ADE80]" />
                    )}
                </MDBox>
                <MDBox className="flex-1 min-w-0">
                    <MDTypography variant="button" fontWeight="bold" color="white" className="block truncate">
                        {getEntityDisplayName(selectedEntity)}
                    </MDTypography>
                    <MDTypography variant="caption" color="white" className="truncate opacity-70">
                        {getEntityTypeName(selectedEntity)}
                        {getEntityCompany(selectedEntity) ? ` · ${getEntityCompany(selectedEntity)}` : ''}
                        <span className="opacity-50"> · ID {getEntityId(selectedEntity)}</span>
                    </MDTypography>
                </MDBox>
                {/* <i className="fa fa-check text-[#4ADE80] shrink-0 mr-1" /> */}
                <MDButton
                    size="small"
                    color="secondary"
                    variant="outlined"
                    onClick={() => onSelect(null)}
                    className="shrink-0 text-xs py-1"
                    sx={{ color: 'white!important', borderColor: 'rgba(255,255,255,0.3)' }}
                >
                    Change
                </MDButton>
            </MDBox>
        );
    }

    // ── Search input + dropdown ────────────────────────────────
    return (
        <MDBox ref={containerRef} className={`relative ${className}`}>
            <MDBox style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                <i className="fa fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none" />
                {loading && (
                    <i className="fa fa-spinner fa-spin absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none" />
                )}
                <input
                    type="text"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    onFocus={() => query.trim() && filteredResults.length > 0 && setShowDropdown(true)}
                    placeholder={placeholder}
                    style={{ width: '100%', paddingLeft: '36px', paddingRight: '36px', paddingTop: '12px', paddingBottom: '12px', backgroundColor: 'rgb(38, 40, 51)', border: '1px solid grey', borderRadius: '8px', color: 'white' }}
                />
            </MDBox>

            {showDropdown && (
                <MDBox className="absolute z-50 mt-1 w-full bg-[#0F2714] border border-[#284D2D] rounded-lg shadow-2xl max-h-72 overflow-y-auto">
                    {filteredResults.length === 0 ? (
                        <MDBox className="px-4 py-4 text-center">
                            <MDTypography variant="caption" color="white" opacity={0.6}>
                                No entities found for &ldquo;{query}&rdquo;
                            </MDTypography>
                        </MDBox>
                    ) : (
                        filteredResults.map(entity => {
                            const id = getEntityId(entity);
                            const typeId = (entity.attributes || entity).entity_type_id;
                            const company = getEntityCompany(entity);
                            return (
                                <button
                                    key={id}
                                    type="button"
                                    onClick={() => handleSelect(entity)}
                                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#1A3F2B] transition text-left border-b border-[#1A2B1A] last:border-0"
                                    style={{ background: 'transparent', borderLeft: 'none', borderRight: 'none', borderTop: 'none' }}
                                >
                                    <MDBox className="shrink-0 w-8 h-8 rounded-full bg-[#1A3F2B] flex items-center justify-center">
                                        {typeId === 2 ? (
                                            <i className="fa fa-building text-[#4ADE80] text-sm" />
                                        ) : (
                                            <i className="fa fa-user text-[#4ADE80] text-sm" />
                                        )}
                                    </MDBox>
                                    <MDBox className="min-w-0 flex-1">
                                        <MDTypography variant="button" fontWeight="medium" color="white" className="block truncate">
                                            {getEntityDisplayName(entity)}
                                        </MDTypography>
                                        <MDTypography variant="caption" color="white" className="truncate opacity-70">
                                            {getEntityTypeName(entity)}
                                            {company ? ` · ${company}` : ''}
                                            <span className="opacity-50"> · ID {id}</span>
                                        </MDTypography>
                                    </MDBox>
                                </button>
                            );
                        })
                    )}
                </MDBox>
            )}
        </MDBox>
    );
};

export default EntitySearchPicker;
